import React, { useMemo } from 'react';
import { Cloud } from 'lucide-react';

const WeatherAnimations = ({ weather, weatherTestMode }) => {
  // Use test mode weather if active, otherwise use real weather
  const displayWeather = weatherTestMode ? {
    temperature: weather?.temperature || 65,
    cloudCover: weatherTestMode === 'clear' ? 0 : 100,
    weatherCode: weatherTestMode === 'clear' ? 0 :
                 weatherTestMode === 'cloudy' ? 3 :
                 weatherTestMode === 'rain' ? 65 :
                 weatherTestMode === 'snow' ? 75 :
                 weatherTestMode === 'thunderstorm' ? 95 :
                 weatherTestMode === 'all' ? 99 : 0
  } : weather;

  // Generate stable cloud properties that don't change on re-render
  const cloudProps = useMemo(() => {
    const cloudCount = displayWeather ? Math.min(Math.ceil(displayWeather.cloudCover / 5), 24) : 0;
    return Array.from({ length: cloudCount }, (_, i) => {
      // Use a seeded random to get consistent but varied properties
      const seed1 = (i * 17 + 13) % 100;
      const seed2 = (i * 23 + 7) % 100;
      const seed3 = (i * 31 + 19) % 100;
      const seed4 = (i * 41 + 29) % 100;
      return {
        width: 60 + seed1 % 120, // Range: 60-180px for more variety
        height: 60 + seed2 % 120,
        top: 5 + seed3 % 85, // Range: 5-90% for more random vertical positions
        duration: 60 + (i * 7) % 80, // Range: 60-140 seconds (much slower)
        delay: -seed4 % 100 // Negative delays to start clouds at random positions
      };
    });
  }, [displayWeather?.cloudCover]);

  // Generate random lightning properties once on mount
  const lightningProps = useMemo(() => {
    return Array.from({ length: 5 }, () => ({
      leftPosition: 5 + Math.random() * 85, // Random position from 5-90%
      topPosition: -10 + Math.random() * 15, // Random top from -10% to 5%
      animationDelay: Math.random() * 15 // Random delay 0-15 seconds
    }));
  }, []);

  return (
    <>
      {/* Animated clouds on page background */}
      {displayWeather && displayWeather.cloudCover > 0 && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {cloudProps.map((cloud, i) => (
            <Cloud
              key={i}
              className="absolute text-blue-200 opacity-20"
              style={{
                width: `${cloud.width}px`,
                height: `${cloud.height}px`,
                top: `${cloud.top}%`,
                left: `-200px`,
                animation: `float-cloud-${i % 5} ${cloud.duration}s linear infinite`,
                animationDelay: `${cloud.delay}s`
              }}
            />
          ))}
        </div>
      )}

      {/* Rain animation */}
      {displayWeather && (weatherTestMode === 'all' || (displayWeather.weatherCode >= 61 && displayWeather.weatherCode <= 67)) && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={`rain-${i}`}
              className="absolute w-0.5 bg-blue-400 opacity-40"
              style={{
                height: `${20 + Math.random() * 10}px`,
                left: `${Math.random() * 100}%`,
                top: `${-20 + Math.random() * 20}%`,
                animation: `fall ${0.5 + Math.random() * 0.5}s linear infinite`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </div>
      )}

      {/* Snow animation */}
      {displayWeather && (weatherTestMode === 'all' || ((displayWeather.weatherCode >= 71 && displayWeather.weatherCode <= 77) || (displayWeather.weatherCode >= 85 && displayWeather.weatherCode <= 86))) && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={`snow-${i}`}
              className="absolute w-2 h-2 rounded-full opacity-70"
              style={{
                background: '#e0f2fe',
                boxShadow: '0 0 4px rgba(186, 230, 253, 0.8)',
                left: `${Math.random() * 100}%`,
                top: `${-10 + Math.random() * 10}%`,
                animation: `snow-fall ${3 + Math.random() * 2}s linear infinite`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
      )}

      {/* Lightning animation */}
      {displayWeather && (weatherTestMode === 'all' || (displayWeather.weatherCode >= 95 && displayWeather.weatherCode <= 99)) && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-5">
          {/* Multiple random lightning bolts with varying intensity and local glow */}
          {lightningProps.map((props, i) => {
            const { leftPosition, topPosition, animationDelay } = props;

            // Generate jagged lightning path
            const startX = 50;
            const startY = 0;
            const segments = 8 + Math.floor(i * 1.5);
            const segmentHeight = 100 / segments;
            let pathD = `M ${startX} ${startY}`;

            for (let j = 1; j <= segments; j++) {
              const y = j * segmentHeight;
              const xOffset = (Math.sin(i * j * 0.7) * 15) + (Math.cos(i * j * 1.3) * 10);
              const x = startX + xOffset;
              pathD += ` L ${x} ${y}`;

              // Add occasional branching
              if (j < segments - 2 && (i + j) % 3 === 0) {
                const branchX = x + (i % 2 === 0 ? 15 : -15);
                const branchY = y + segmentHeight * 0.5;
                pathD += ` M ${x} ${y} L ${branchX} ${branchY}`;
                pathD += ` M ${x} ${y}`;
              }
            }

            return (
              <div
                key={`lightning-container-${i}`}
                className="absolute"
                style={{
                  width: '300px',
                  height: '40vh',
                  left: `calc(${leftPosition}% - 100px)`,
                  top: `${topPosition}%`,
                }}
              >
                {/* Localized glow around bolt */}
                <div
                  className="absolute inset-0 opacity-0"
                  style={{
                    background: 'radial-gradient(ellipse at center, rgba(254, 240, 138, 0.3) 0%, rgba(254, 240, 138, 0.15) 30%, transparent 60%)',
                    animation: `lightning-glow-${i} 8s linear infinite`,
                    animationDelay: `${animationDelay}s`
                  }}
                />
                <svg
                  className="absolute opacity-0"
                  style={{
                    width: '100px',
                    height: '100%',
                    left: '100px',
                    filter: `brightness(${1.2 + i * 0.2}) drop-shadow(0 0 ${12 + i * 4}px rgba(253, 224, 71, 1))`,
                    animation: `lightning-bolt-${i} 8s linear infinite`,
                    animationDelay: `${animationDelay}s`
                  }}
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  <path
                    d={pathD}
                    stroke="#fef08a"
                    strokeWidth={2 + (i % 2)}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            );
          })}
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes float-cloud-0 {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(100vw + 100px)); }
        }
        @keyframes float-cloud-1 {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(100vw + 100px)); }
        }
        @keyframes float-cloud-2 {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(100vw + 100px)); }
        }
        @keyframes float-cloud-3 {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(100vw + 100px)); }
        }
        @keyframes float-cloud-4 {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(100vw + 100px)); }
        }
        @keyframes fall {
          0% { transform: translateY(0); }
          100% { transform: translateY(100vh); }
        }
        @keyframes snow-fall {
          0% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(50vh) translateX(20px); }
          100% { transform: translateY(100vh) translateX(0); }
        }
        @keyframes lightning-glow-0 {
          0%, 100% { opacity: 0; }
          10.3% { opacity: 0.6; }
          10.6% { opacity: 0; }
          11% { opacity: 0.7; }
          11.2% { opacity: 0; }
        }
        @keyframes lightning-glow-1 {
          0%, 100% { opacity: 0; }
          10.5% { opacity: 0.5; }
          10.7% { opacity: 0; }
        }
        @keyframes lightning-glow-2 {
          0%, 100% { opacity: 0; }
          61.8% { opacity: 0.7; }
          62.1% { opacity: 0; }
          62.3% { opacity: 0.5; }
          62.5% { opacity: 0; }
        }
        @keyframes lightning-glow-3 {
          0%, 100% { opacity: 0; }
          62% { opacity: 0.6; }
          62.2% { opacity: 0; }
        }
        @keyframes lightning-glow-4 {
          0%, 100% { opacity: 0; }
          35% { opacity: 0.7; }
          35.3% { opacity: 0; }
          35.5% { opacity: 0.55; }
          35.7% { opacity: 0; }
        }
        @keyframes lightning-bolt-0 {
          0%, 100% { opacity: 0; filter: brightness(1); }
          10.3% { opacity: 1; filter: brightness(2); }
          10.4% { opacity: 0.7; filter: brightness(1.5); }
          10.6% { opacity: 0; filter: brightness(1); }
          11% { opacity: 0.95; filter: brightness(2.2); }
          11.1% { opacity: 0.5; filter: brightness(1.3); }
          11.2% { opacity: 0; filter: brightness(1); }
        }
        @keyframes lightning-bolt-1 {
          0%, 100% { opacity: 0; filter: brightness(1); }
          10.5% { opacity: 0.9; filter: brightness(1.8); }
          10.6% { opacity: 0.4; filter: brightness(1.2); }
          10.7% { opacity: 0; filter: brightness(1); }
        }
        @keyframes lightning-bolt-2 {
          0%, 100% { opacity: 0; filter: brightness(1); }
          61.8% { opacity: 1; filter: brightness(2.5); }
          61.9% { opacity: 0.6; filter: brightness(1.6); }
          62.1% { opacity: 0; filter: brightness(1); }
          62.3% { opacity: 0.8; filter: brightness(1.9); }
          62.4% { opacity: 0.3; filter: brightness(1.1); }
          62.5% { opacity: 0; filter: brightness(1); }
        }
        @keyframes lightning-bolt-3 {
          0%, 100% { opacity: 0; filter: brightness(1); }
          62% { opacity: 0.95; filter: brightness(2.1); }
          62.1% { opacity: 0.5; filter: brightness(1.4); }
          62.2% { opacity: 0; filter: brightness(1); }
        }
        @keyframes lightning-bolt-4 {
          0%, 100% { opacity: 0; filter: brightness(1); }
          35% { opacity: 1; filter: brightness(2.3); }
          35.15% { opacity: 0.55; filter: brightness(1.5); }
          35.3% { opacity: 0; filter: brightness(1); }
          35.5% { opacity: 0.85; filter: brightness(2); }
          35.6% { opacity: 0.4; filter: brightness(1.2); }
          35.7% { opacity: 0; filter: brightness(1); }
        }
      `}</style>
    </>
  );
};

export default WeatherAnimations;
